import { DownOutlined, VerticalAlignTopOutlined } from "@ant-design/icons";
import {
  Button,
  Dropdown,
  MenuProps,
  Tooltip,
  Modal,
  notification,
  Tag
} from "antd";
import { message } from "utils/ciq-message";
import CiqAgSearch from "components/ciq-ag-search";
import ColumnConfigViews, {
  TSaveColumnState
} from "components/column-config-views";
import { UtilColumnConfig } from "components/column-config-views/column-config-util";
import ImportFeatureLogs from "components/import-feature-logs";
import AddEntityLogButton, {
  AddEntityMenuItem
} from "components/add-entity-log-button";
import { CiqIconBox } from "components/svg-icons";
import {
  EFeaturePageId,
  ErrorMessages,
  InfoMessages,
  ProjectPermissionEnum,
  SidepanelStyles,
  SuccessMessages
} from "constants/index";
import { AppContext } from "context/AppProvider";
import {
  isPermissionNotGrantted,
  ProjectContext
} from "context/ProjectProvider";
import {
  FilterChipComponent,
  FilterChipComponentRef
} from "entity-app/shared-components/log-render-components/filter-chip-component";
import { exportEntityLogAsCSV } from "entity-app/utils/export-entity-log-as-csv";
import {
  exportEntityLogAsExcel,
  sanitizeSheetName
} from "components/excel-ag-grid-export/export-entity-log-as-excel";
import { resetGridFilters } from "entity-app/utils/utils";
import { ExcelExportOptions } from "components/excel-ag-grid-export/excel-export-option";
import ExportOptionsPopover, {
  ExportFormatOption,
  ExportSelection
} from "components/export-options-popover";
import {
  MaterialLogPrintViewState,
  PRINT_VIEW_STORAGE_KEY
} from "pages/print-view-layout/model";
import { useCallback, useContext, useMemo, useState } from "react";
import { newEntityNotificationMessage } from "entity-app/shared-components/new-entity-notification-message";
import { AgGridReact } from "ag-grid-react";
import { useHistory } from "react-router";
import FeatureBulkEditDurationsNewModel from "components/feature-bulk-edit-durations/feature-bulk-edit-durations-new-model";
import ErrorBoundary from "components/error-boundary";
import { FeatureTypes } from "entity-app/constants";
import BulkFeatureEditMaterialDetails from "components/bulk-edit-material/bulk-feature-edit-material-details";
import RequestDurationsMulti from "components/request-durations/request-durations-multi/request-durations-multi";
import { getGridStatusBudge } from "components/ciq-log-page-header/grid-status-budge";
import BulkFeatureEditMaterialStatus from "components/bulk-edit-material/bulk-feature-edit-material-status";
import BulkFeatureEditSubmittalPlan from "components/bulk-edit-material/bulk-feature-edit-submittal-plan";
import BulkEditDeadlineDateModal from "components/bulk-edit-dateline-date/bulk-edit-dateline-date";
import PrintViewValidationModal from "pages/print-view-layout/Print-View-Validation-Modal";
import { DateUtils } from "utils/dateutils";
import AiPoweredUI from "components/welcome-startup-setup/ai-powered";
import {
  CUSTOM_EXCEL_EXPORT_FLAG,
  openCustomExcelExport
} from "features/custom-excel-export/constants";
import CreateMaterialEntityComponent from "./create-material-entity";
import { TMaterialLog } from "../model";
import {
  attachCommentsToRows,
  fetchCommentsByMaterialId
} from "../material-comments";
import useAuditEventLog from "entity-app/utils/export-audit-log";
import { COMMENTS_EXPORT_COLUMN_ID } from "utils/comments-export";

/** Hidden in the grid; added to an export only when Include Comments is checked. */
const EXPORT_ONLY_COLUMN_IDS = [COMMENTS_EXPORT_COLUMN_ID];

type Prop = {
  selectedRows: TMaterialLog[] | null;
  gridRef: React.RefObject<AgGridReact<any>>;
  filterChipRef: React.RefObject<FilterChipComponentRef>;
  pageData: {
    entityList: TMaterialLog[] | undefined;
    fetchLogData: () => Promise<void>;
    materialHeaderMap: any;
    mutationUpdateFeaturesData: (payload: any) => Promise<any>;
  };
  featureKey: string;
  projectId: string;
  statsData:
    | {
        displayCount: number;
        filterName: undefined | string;
      }
    | undefined;
  pageSidebarState: {
    isSidebarOpen: boolean;
    sidebarAction: (params: {
      feature: string;
      action: "open" | "close";
      itemDetailsToSet: any | null;
    }) => void;
  };
  afterApplySavedViewCallback?: () => void;
  isMaterialCsiClassificationEnabled: boolean;
  searchText: string;
  onSearch: (text: string) => void;
  setSearchText: (text: string) => void;
  getConditionalsFormatting?: () => Array<any>;
  onConditionalsFormattingChange?: (rules: Array<any>) => void;
};

export default function MaterialHeaderItems({
  selectedRows,
  gridRef,
  pageData,
  filterChipRef,
  statsData,
  featureKey,
  projectId,
  pageSidebarState = {
    isSidebarOpen: false,
    sidebarAction: () => {}
  },
  afterApplySavedViewCallback,
  isMaterialCsiClassificationEnabled,
  searchText,
  onSearch,
  setSearchText,
  getConditionalsFormatting,
  onConditionalsFormattingChange
}: Readonly<Prop>) {
  const {
    projectDetails,
    isFeatureFlagEnabled,
    tokenContents,
    tokenRetrievalState,
    gqlClientForProject,
    isProjectArchived
  } = useContext(ProjectContext);
  const isExportDisabled = !pageData.entityList || !!isProjectArchived;
  const [isFetchingComments, setIsFetchingComments] = useState(false);
  const { saveAuditLogEvent } = useAuditEventLog(gqlClientForProject);
  const { materialListFilter, setMaterialListFilter } = useContext(AppContext);

  const history = useHistory();
  const [bulkEditDialog, onMaterialEditClick] = useState<
    | "details"
    | "duration"
    | "request_durations_from_sc"
    | null
    | "status"
    | "deadline_date"
    | "submittal_plan"
  >(null);
  const [showImportLogDrawer, setShowImportLogDrawer] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);

  const [api, contextHolder] = notification.useNotification();
  const openNotification = (id: string, title: string) => {
    api.open(
      newEntityNotificationMessage({
        projectId,
        id,
        title,
        featureKey
      })
    );
  };

  const isRequestLeadTimeFeatureEnabled =
    isFeatureFlagEnabled("REQUEST_LEAD_TIME");

  const isMaterialBulkLinkingEnabled = isFeatureFlagEnabled(
    "MATERIAL_MULTIPLE_LINKING"
  );

  const isPrintViewEnabled = isFeatureFlagEnabled("PRINT_VIEW");
  const isCustomExcelExportEnabled = isFeatureFlagEnabled(
    CUSTOM_EXCEL_EXPORT_FLAG
  );
  const isExcelExportEnabled = isFeatureFlagEnabled("EXCEL_EXPORT");

  const cannotCreateMaterial: any = isPermissionNotGrantted(
    ProjectPermissionEnum.CreateMaterial,
    tokenContents?.role ?? ""
  );

  const isMaterialExtractionEnabled =
    isFeatureFlagEnabled("MATERIAL_EXTRACTION") && !cannotCreateMaterial;

  const cannotRequestLeadTime: any = isPermissionNotGrantted(
    ProjectPermissionEnum.RequestLeadTime,
    tokenContents?.role ?? ""
  );

  const onAllMaterialClick = useCallback(() => {
    resetGridFilters({
      setListFilter: setMaterialListFilter,
      api: gridRef.current?.api
    });
  }, [gridRef, setMaterialListFilter]);

  const materialsTypeMenu: MenuProps = useMemo(
    () => ({
      className: "max-w-sm",
      items: [
        {
          key: "material_log",
          onClick: () => history.replace(`/project/${projectId}/materials`),
          className: "px-4 py-2",
          label: (
            <div className="flex items-center space-x-3">
              <CiqIconBox feature="MATERIALS" size={20} padding={4} />
              <div>
                <div className="font-medium text-black text-opacity-90">
                  Material Log
                </div>
                <div className="text-ciq-gray-900 text-xs">
                  Plan and track Material delivery
                </div>
              </div>
            </div>
          )
        },
        isMaterialExtractionEnabled
          ? {
              key: "extracted_from_pdf",
              onClick: () =>
                history.replace(`/project/${projectId}/materials/extracted`),
              className: "px-4 py-2",
              label: (
                <div className="flex items-center justify-between space-x-3">
                  <div className="flex items-center space-x-3">
                    <CiqIconBox
                      feature="EXTRACTED_MATERIALS"
                      size={20}
                      padding={4}
                    />
                    <div>
                      <div className="flex items-center justify-between space-x-3">
                        <div className="flex items-center space-x-2 font-medium text-black text-opacity-90">
                          <span>Material Extraction</span>
                          <Tag>Beta</Tag>
                        </div>
                        <div className="shrink-0">
                          <AiPoweredUI />
                        </div>
                      </div>
                      <div className="text-ciq-gray-900 text-xs">
                        Extract Materials from Drawings and select the ones that
                        you want to track
                      </div>
                    </div>
                  </div>
                </div>
              )
            }
          : null
      ]
    }),
    [history, projectId, isMaterialExtractionEnabled]
  );

  const addMaterialsItems: AddEntityMenuItem[] = useMemo(
    () => [
      {
        key: "create_new",
        title: "Manually create a Material",
        description: "Create a single Material manually",
        onClick: () => setShowDrawer(true)
      },
      {
        key: "import_log",
        title: "Import Materials",
        description: "Upload a CSV to add or update Materials",
        onClick: () => setShowImportLogDrawer(true)
      },
      ...(isMaterialExtractionEnabled
        ? [
            {
              key: "extract_from_drawings",
              title: (
                <div className="flex items-center space-x-2">
                  <span>Extract from Drawings</span>
                  <Tag>Beta</Tag>
                </div>
              ),
              extra: <AiPoweredUI label="AI powered" />,
              descriptionClassName: "max-w-xs",
              description:
                "Create Materials from extracted schedules or extract more from your project drawings",
              onClick: () => {
                history.replace(
                  `/project/${projectDetails?.id}/materials/extracted`
                );
              }
            } as AddEntityMenuItem
          ]
        : [])
    ],
    [history, projectDetails?.id, isMaterialExtractionEnabled]
  );

  const bulkEditActionMenu: MenuProps = useMemo(() => {
    return {
      items: [
        {
          type: "group",
          key: "bulk-edit",
          label: "Bulk Edit",
          children: [
            {
              key: "deadline_date",
              onClick: () => onMaterialEditClick("deadline_date"),
              label:
                pageData?.materialHeaderMap?.manual_entry_date ||
                "Deadline Date"
            },
            {
              key: "details",
              onClick: () => onMaterialEditClick("details"),
              label: "Details"
            },
            {
              key: "durations",
              onClick: () => onMaterialEditClick("duration"),
              label: "Durations"
            },
            isMaterialBulkLinkingEnabled
              ? {
                  key: "links",
                  onClick: () =>
                    history.push(
                      `/project/${projectDetails?.id}/schedule/material-linking`,
                      {
                        name: "Bulk Edit Links",
                        selectedRows: selectedRows?.map((x) => ({
                          ...x,
                          name: x.title
                        }))
                      }
                    ),
                  label: "Links"
                }
              : null,
            {
              key: "status",
              onClick: () => onMaterialEditClick("status"),
              label: "Status"
            }
          ]
        },
        { type: "divider" },
        {
          key: "submittal_plan",
          onClick: () => onMaterialEditClick("submittal_plan"),
          label: "Create Submittal Plan"
        },
        isRequestLeadTimeFeatureEnabled && !cannotRequestLeadTime
          ? {
              key: "request_lead_times",
              onClick: () => onMaterialEditClick("request_durations_from_sc"),
              label: "Request Lead Time/Status"
            }
          : null
      ]
    };
  }, [
    history,
    projectDetails?.id,
    selectedRows,
    isMaterialBulkLinkingEnabled,
    isRequestLeadTimeFeatureEnabled,
    cannotRequestLeadTime,
    pageData?.materialHeaderMap?.manual_entry_date
  ]);

  const deleteMaterials = useCallback(() => {
    Modal.confirm({
      title: InfoMessages.deleteMaterialConfirmTitle,
      okText: "Yes",
      cancelText: "No",
      async onOk() {
        try {
          if (!selectedRows) return;
          const payload = {
            variables: {
              feature_instance_ids: selectedRows.map((s: any) => s.id),
              feature_type_id: 2
            }
          };
          const deleteMaterialsRes =
            await pageData.mutationUpdateFeaturesData(payload);
          if (deleteMaterialsRes.data) {
            message.success(SuccessMessages.deleteSuccess);
            pageData.fetchLogData();
          }
          if (deleteMaterialsRes.errors) {
            console.error(deleteMaterialsRes.errors);
            message.error(ErrorMessages.deleteMaterialFailed);
          }
        } catch (ex: any) {
          const errorMsg = ex.message || ErrorMessages.deleteMaterialFailed;
          message.error(errorMsg);
        }
      }
    });
  }, [pageData, selectedRows]);

  const onOpenPrintView = useCallback(
    (includeComments = false) => {
      if (!gridRef.current || !projectDetails) {
        message.error("Unable to open print view. Grid data is not available.");
        return;
      }

      try {
        sessionStorage.clear();

        const context = gridRef?.current?.props?.context || {};
        const contextData = {
          projectDetails: context.projectDetails,
          isGridCellEditable: false,
          editPermission: false,
          canInlineEditMaterialGeneralInfoAndParticipants: false,
          canInlineEditMaterialCharacteristics: false,
          canInlineEditDBOffset: false,
          cannotChangeActualDate: true,
          canSetLBSForMaterial: false,
          LBSOptionsMaster: context.LBSOptionsMaster,
          LBSDataInTreeFormat: context.LBSDataInTreeFormat,
          material_quantity_unit: context.material_quantity_unit,
          isRequestLeadTimeFeatureEnabled:
            context.isRequestLeadTimeFeatureEnabled,
          isMaterialExtractionEnabled: context.isMaterialExtractionEnabled,
          canRequestLeadTime: context.canRequestLeadTime
        };

        const initialState = gridRef.current.api.getState();
        const printViewState: MaterialLogPrintViewState = {
          pageId: "material-log",
          title: `${
            projectDetails?.name || "Project"
          } - Material Log - Exported from ConstructivIQ on ${DateUtils.dateTimeObj().format(
            `M/D [at] h:mma [${DateUtils.dateTimeObj().format("z")}]`
          )}`,
          initialState,
          context: contextData,
          skipColumnIds: ["action"],
          includeComments,
          forceVisibleColumnIds: includeComments ? EXPORT_ONLY_COLUMN_IDS : [],
          headerHeight: gridRef.current.props.headerHeight,
          // Comments wrap via autoHeight; a fixed height clips multi-line cells.
          rowHeight: includeComments
            ? undefined
            : gridRef.current.props.rowHeight,
          conditionalsFormatting: getConditionalsFormatting?.() ?? []
        };

        sessionStorage.setItem(
          PRINT_VIEW_STORAGE_KEY,
          JSON.stringify(printViewState)
        );

        const printViewUrl = `/project/${projectId}/printView`;
        window.open(printViewUrl, "_blank");
      } catch (error) {
        console.error("Error opening material log print view:", error);
        message.error("An error occurred while opening print view.");
      }
    },
    [gridRef, projectDetails, projectId, getConditionalsFormatting]
  );

  /** Returns null (after showing an error) when comments can't be fetched. */
  const loadCommentsForExport = useCallback(async () => {
    setIsFetchingComments(true);
    try {
      const commentsById = await fetchCommentsByMaterialId(
        tokenRetrievalState?.token
      );
      if (!commentsById)
        message.error("Unable to fetch comments. Please try again.");
      return commentsById;
    } finally {
      setIsFetchingComments(false);
    }
  }, [tokenRetrievalState?.token]);

  const onExportExcel = useCallback(
    (
      options: ExcelExportOptions,
      commentsById: Map<string, string[]> | null = null
    ) => {
      if (!gridRef.current || !projectDetails) return;
      const filename: string = `Material_log_${sanitizeSheetName(projectDetails.name)}.xlsx`;
      if (commentsById) attachCommentsToRows(gridRef.current.api, commentsById);
      exportEntityLogAsExcel({
        projectDetails,
        gridRef,
        filename,
        prependText: `${
          projectDetails.name
        } - Material Log - Exported from ConstructivIQ on ${DateUtils.dateTimeObj().format(
          `M/D [at] h:mma [${DateUtils.dateTimeObj().format("z")}]`
        )}`,
        includedColumns: [
          "description",
          ...(commentsById ? EXPORT_ONLY_COLUMN_IDS : [])
        ],
        excludedColumns: ["_milestones", "_durations", "action"],
        sortPosition: [
          {
            colId: "released_early_msg",
            dependentColId: "material_released_early",
            position: "after"
          }
        ],
        conditionalsFormatting: getConditionalsFormatting?.() as any,
        options
      });

      saveAuditLogEvent({
        eventType: "MATERIAL_LOG_EXPORT_EXCEL",
        featureType: "MATERIALS",
        filename
      });
    },
    [gridRef, projectDetails, getConditionalsFormatting, saveAuditLogEvent]
  );

  const bulkEditButton = useMemo(() => {
    if (!selectedRows) return null;
    const selectedCount = selectedRows.length;
    if (selectedCount < 1) return null;
    return (
      <div
        className="bg-ciq-gray-100 bg-opacity-70 py-1 px-4 flex items-center space-x-2 rounded-md"
        key="MaterialLogPage_BulkEdit"
      >
        <div className="py-1 whitespace-nowrap" key="MaterialLogPage_Selected">
          {selectedRows?.length} Selected
        </div>
        {selectedCount > 1 && (
          <Dropdown
            menu={bulkEditActionMenu}
            key="MaterialLogPage_bulkEdit"
            disabled={cannotCreateMaterial}
          >
            <Button
              disabled={cannotCreateMaterial}
              title={
                cannotCreateMaterial
                  ? ErrorMessages.PermissionNotGranted
                  : "Actions"
              }
            >
              Actions <DownOutlined />
            </Button>
          </Dropdown>
        )}
        <Button
          key="MaterialLogPage_Delete"
          onClick={deleteMaterials}
          size="middle"
          className="bottomBarBtn"
          title={
            cannotCreateMaterial ? ErrorMessages.PermissionNotGranted : "Delete"
          }
          disabled={cannotCreateMaterial}
        >
          Delete
        </Button>
      </div>
    );
  }, [bulkEditActionMenu, cannotCreateMaterial, deleteMaterials, selectedRows]);

  const applyColumnConfig = useCallback(
    (columnStates: TSaveColumnState) => {
      if (!gridRef.current) return;
      let updatedColumnStates = columnStates;
      if (isMaterialCsiClassificationEnabled && columnStates?.columnList) {
        const hasDivisionConfig = columnStates.columnList.some(
          (col: any) => col?.field?.name === "division"
        );
        if (!hasDivisionConfig) {
          const maxOrder =
            columnStates.columnList.reduce((acc: number, col: any) => {
              const order = typeof col?.order === "number" ? col.order : 0;
              return Math.max(acc, order);
            }, 0) || 0;
          const specSectionCol = columnStates.columnList.find(
            (col: any) => col?.field?.name === "custom_spec_section"
          );
          const divisionOrder = specSectionCol?.order
            ? specSectionCol.order + 0.5
            : maxOrder + 1;

          updatedColumnStates = {
            ...columnStates,
            columnList: [
              ...columnStates.columnList,
              {
                field: {
                  name: "division",
                  range: null,
                  width: null,
                  filter: null,
                  agFilter: null,
                  sortOrder: null
                },
                order: divisionOrder,
                visibility: true
              }
            ]
          };
        }
      }
      UtilColumnConfig.setFilterStateFromData({
        columnStates: updatedColumnStates,
        gridRef: {
          api: gridRef.current.api
        },
        restoreRowGrouping: isMaterialCsiClassificationEnabled
      });
      setSearchText(updatedColumnStates?.quickSearch ?? "");
      onConditionalsFormattingChange?.(
        updatedColumnStates?.conditionals_formatting ?? []
      );
    },
    [
      gridRef,
      isMaterialCsiClassificationEnabled,
      onConditionalsFormattingChange,
      setSearchText
    ]
  );

  const headerItems = useMemo(() => {
    const menus = [] as JSX.Element[];
    if (!materialListFilter)
      menus.push(
        <FilterChipComponent
          key="MaterialLogPage_FilterChip"
          gridRef={gridRef}
          ref={filterChipRef}
          onSearchTextClear={() => setSearchText("")}
          data-testid="material-header-filter-chip"
        />
      );
    if (bulkEditButton && !pageSidebarState.isSidebarOpen)
      menus.push(bulkEditButton);
    menus.push(
      <CiqAgSearch
        key="MaterialLogPage_search"
        gridRef={gridRef}
        placeholder="Search Materials"
        value={searchText}
        onSearch={onSearch}
        data-testid="material-header-search"
      />
    );

    menus.push(
      <ColumnConfigViews
        key="MaterialLogPage_ColumnConfigViews"
        featureId={EFeaturePageId.MATERIAL}
        setFilterStateFromData={applyColumnConfig}
        getColumnStateFromGrid={() => {
          if (gridRef.current) {
            return UtilColumnConfig.getColumnStateFromGrid({
              gridRef: {
                api: gridRef.current!.api
              },
              isIncludeSearchText: false,
              conditionals_formatting: getConditionalsFormatting?.() ?? []
            });
          }
          return null;
        }}
        afterApplyFilterCallBack={afterApplySavedViewCallback}
        data-testid="material-header-column-config"
      />
    );

    if (pageSidebarState.isSidebarOpen) {
      menus.push(
        <Tooltip
          title="More Actions available"
          placement="topRight"
          key="close_sidepanel_tooltip"
        >
          <Button
            key="close_sidepanel"
            data-testid="close-sidepanel-button"
            onClick={() => {
              pageSidebarState.sidebarAction({
                feature: "",
                action: "close",
                itemDetailsToSet: null
              });
            }}
          >
            <VerticalAlignTopOutlined rotate={90} />
          </Button>
        </Tooltip>
      );
      return menus;
    }

    const onExportCsv = (commentsById: Map<string, string[]> | null = null) => {
      if (projectDetails && gridRef.current) {
        const gridApi = gridRef.current.api;
        const filename = `Materials_log_${projectDetails.name}.csv`;
        if (commentsById) attachCommentsToRows(gridApi, commentsById);
        exportEntityLogAsCSV({
          filename,
          gridRef: {
            api: gridApi
          },
          includedColumns: [
            "description",
            ...(commentsById ? EXPORT_ONLY_COLUMN_IDS : [])
          ],
          excludedColumns: ["_milestones", "_durations", "action"],
          skipRowGroups: true,
          sortPosition: [
            {
              colId: "released_early_msg",
              dependentColId: "material_released_early",
              position: "after"
            }
          ]
        });

        saveAuditLogEvent({
          eventType: "MATERIAL_LOG_EXPORT_CSV",
          featureType: "MATERIALS",
          filename
        });
      }
    };

    const exportOptions: ExportFormatOption[] = [
      {
        key: "export_csv",
        title: "CSV",
        description: "Download the log as a CSV file",
        testId: "material-header-export-csv"
      }
    ];
    if (isExcelExportEnabled) {
      exportOptions.push({
        key: "export_excel",
        title: "Excel (.xlsx)",
        description: "Download the log as an Excel file",
        isExcel: true,
        disabled: !pageData.entityList?.length,
        testId: "material-header-export-excel"
      });
    }
    if (isPrintViewEnabled) {
      exportOptions.push({
        key: "print_view",
        title: "PDF/Print",
        description: "Open a print-friendly view",
        testId: "material-header-print-view"
      });
    }
    if (isCustomExcelExportEnabled) {
      exportOptions.push({
        key: "export_custom",
        title: "Export Custom",
        description: "Build a custom Excel export in a new tab",
        supportsIncludeComments: false,
        testId: "material-header-export-custom"
      });
    }

    const onProceedExport = async ({
      key,
      excelOptions,
      includeComments
    }: ExportSelection) => {
      if (key === "print_view") {
        if (gridRef.current)
          PrintViewValidationModal({
            gridApi: gridRef.current.api,
            onProceed: () => onOpenPrintView(includeComments),
            skipColumnIds: ["action", "ag-Grid-SelectionColumn"],
            forceVisibleColumnIds: includeComments ? EXPORT_ONLY_COLUMN_IDS : []
          });
        return;
      }
      if (key === "export_custom") {
        openCustomExcelExport(projectId);
        return;
      }
      if (key === "export_excel" && !excelOptions) return;

      let commentsById: Map<string, string[]> | null = null;
      if (includeComments) {
        commentsById = await loadCommentsForExport();
        if (!commentsById) return;
      }

      if (key === "export_excel") {
        onExportExcel(excelOptions!, commentsById);
        return;
      }
      onExportCsv(commentsById);
    };

    menus.push(
      <ExportOptionsPopover
        key="MaterialLogPage_export"
        options={exportOptions}
        onProceed={onProceedExport}
        excelTableName={projectDetails?.name || "Project"}
        disabled={isExportDisabled}
        loading={isFetchingComments}
        buttonType="default"
        buttonTestId="material-header-export"
      />
    );

    menus.push(
      <AddEntityLogButton
        key="MaterialLogPage_addMaterials"
        buttonLabel="Add Material"
        items={addMaterialsItems}
        disabled={cannotCreateMaterial}
        disabledTitle={ErrorMessages.PermissionNotGranted}
        enabledTitle="Add Material"
        data-testid="material-header-add-materials"
      />
    );

    return menus;
  }, [
    applyColumnConfig,
    isCustomExcelExportEnabled,
    projectId,
    materialListFilter,
    gridRef,
    filterChipRef,
    bulkEditButton,
    pageSidebarState,
    afterApplySavedViewCallback,
    getConditionalsFormatting,
    isExcelExportEnabled,
    isPrintViewEnabled,
    addMaterialsItems,
    cannotCreateMaterial,
    projectDetails,
    saveAuditLogEvent,
    pageData.entityList,
    isExportDisabled,
    isFetchingComments,
    loadCommentsForExport,
    onOpenPrintView,
    onExportExcel,
    onSearch,
    searchText,
    setSearchText
  ]);

  const statusBudge = useMemo(
    () =>
      getGridStatusBudge(
        {
          title: "Materials",
          totalCount: pageData.entityList?.length,
          filterStats: statsData
        },
        onAllMaterialClick
      ),
    [onAllMaterialClick, pageData.entityList?.length, statsData]
  );

  const materialsTitleDropdown = (
    <div
      className="flex items-center justify-start cursor-pointer"
      data-testid="material-header-title"
    >
      <CiqIconBox feature="MATERIALS" size={20} padding={8} />
      <div className="flex flex-col pl-3">
        <Dropdown
          menu={materialsTypeMenu}
          trigger={isMaterialExtractionEnabled ? ["hover"] : []}
          placement="bottomLeft"
        >
          <div className="flex items-center gap-2">
            <span className="text-black text-xl font-semibold">Materials</span>
            {isMaterialExtractionEnabled && (
              <DownOutlined className="text-xs self-center mt-0.5" />
            )}
          </div>
        </Dropdown>
      </div>
      {statusBudge && (
        <div className="flex shrink-0 ml-2" data-testid="material-header-stats">
          {statusBudge}
        </div>
      )}
    </div>
  );

  return (
    <div
      className="w-full px-6 py-4 flex justify-between items-center"
      data-testid="material-header-items-container"
    >
      <div className="flex space-x-2 items-center text-black">
        {materialsTitleDropdown}
      </div>

      <div
        className="flex space-x-2 items-center"
        data-testid="material-header-actions"
      >
        {headerItems}
      </div>

      {contextHolder}

      {showDrawer && (
        <CreateMaterialEntityComponent
          setDrawerOpen={setShowDrawer}
          showDrawerOpen={showDrawer}
          modelTitle="Create Material"
          onCreated={(title: string, id: string) => {
            setShowDrawer(false);
            openNotification(id, title);
            pageData.fetchLogData();
          }}
        />
      )}

      {showImportLogDrawer && (
        <ImportFeatureLogs
          open={showImportLogDrawer}
          featureType={FeatureTypes.MATERIALS}
          entityLabelSingular="Material"
          entityLabelPlural="Materials"
          showWorkflowTemplates
          standardTemplate={{
            headers: [
              pageData.materialHeaderMap?.name || "",
              "Spec Section #",
              "Spec Section Name",
              "Material Description",
              "Quantity",
              "Material Tag",
              "Material Size",
              "Manufacturer",
              "Part Number",
              "Serial Number"
            ],
            fileName: "Sample_Material_Log.csv"
          }}
          onCancel={() => {
            setShowImportLogDrawer(false);
          }}
          onComplete={() => {
            pageData.fetchLogData();
          }}
        />
      )}

      <Modal
        title="Bulk Edit Durations"
        className="custom-drawer with-padding-fix"
        style={SidepanelStyles.style}
        width="calc(100vw - 200px)"
        footer={[
          <div className="flex w-full space-x-3 justify-end" key={100}>
            <Button
              onClick={() => {
                onMaterialEditClick(null);
                gridRef?.current?.api?.deselectAll();
              }}
              key={101}
            >
              Close
            </Button>
          </div>
        ]}
        open={bulkEditDialog === "duration"}
        onCancel={() => {
          onMaterialEditClick(null);
        }}
        destroyOnHidden
        keyboard={false}
      >
        <ErrorBoundary>
          <FeatureBulkEditDurationsNewModel
            selectedRows={selectedRows!}
            featureTypeId={FeatureTypes.MATERIALS}
            onUpdateDone={() => {
              pageData.fetchLogData();
            }}
          />
        </ErrorBoundary>
      </Modal>

      {selectedRows && bulkEditDialog === "details" && (
        <BulkFeatureEditMaterialDetails
          onCancelClick={() => {
            onMaterialEditClick(null);
          }}
          onUpdateDone={() => {
            onMaterialEditClick(null);
            gridRef?.current?.api?.deselectAll();
            pageData.fetchLogData();
          }}
          setShowEdit={onMaterialEditClick}
          showEdit={bulkEditDialog === "details"}
          selectedRows={selectedRows}
        />
      )}

      {selectedRows && bulkEditDialog === "status" && (
        <BulkFeatureEditMaterialStatus
          onCancelClick={() => {
            onMaterialEditClick(null);
          }}
          onUpdateDone={() => {
            onMaterialEditClick(null);
            gridRef?.current?.api?.deselectAll();
            pageData.fetchLogData();
          }}
          setShowEdit={onMaterialEditClick}
          showEdit={bulkEditDialog === "status"}
          selectedRows={selectedRows}
        />
      )}

      {selectedRows && bulkEditDialog === "deadline_date" && (
        <BulkEditDeadlineDateModal
          onUpdateDone={() => {
            onMaterialEditClick(null);
            gridRef?.current?.api?.deselectAll();
            pageData.fetchLogData();
          }}
          setShowEdit={onMaterialEditClick}
          showEdit={bulkEditDialog === "deadline_date"}
          selectedRows={selectedRows}
          columnHeaderMap={pageData.materialHeaderMap}
          featureTypeId={FeatureTypes.MATERIALS}
        />
      )}

      {selectedRows && bulkEditDialog === "submittal_plan" && (
        <BulkFeatureEditSubmittalPlan
          selectedRows={selectedRows}
          showEdit={bulkEditDialog === "submittal_plan"}
          setShowEdit={(v) => {
            if (v) {
              onMaterialEditClick(v);
            } else {
              onMaterialEditClick(null);
              gridRef?.current?.api?.deselectAll();
            }
          }}
          onUpdateDone={() => {
            onMaterialEditClick(null);
            gridRef?.current?.api?.deselectAll();
            pageData.fetchLogData();
          }}
        />
      )}

      <Modal
        title={
          <div>
            <div className="font-semibold text-sm">
              Request Lead Time/Status
            </div>
            <div className="text-xs mt-0.5">
              <div>
                This will send emails to the Assigned Subcontractors with a link
                to provide:
              </div>
              <ul className="pl-4">
                <li>Lead Time for Materials not yet started, OR</li>
                <li>Update the status for Materials in progress.</li>
                <li>
                  Committed Dates for Materials using Committed Date Logic.
                </li>
              </ul>
            </div>
          </div>
        }
        open={bulkEditDialog === "request_durations_from_sc"}
        styles={{ body: { padding: 0 } }}
        width={1000}
        footer={null}
        onCancel={() => {
          onMaterialEditClick(null);
        }}
        destroyOnHidden
        centered
        className="request-durations-modal"
      >
        <div className="h-[560px] overflow-y-hidden">
          {selectedRows && selectedRows.length > 0 && (
            <RequestDurationsMulti
              selectedItems={selectedRows}
              onCancel={() => {
                onMaterialEditClick(null);
              }}
              onComplete={() => {
                onMaterialEditClick(null);
                gridRef.current?.api?.deselectAll();
              }}
            />
          )}
        </div>
      </Modal>
    </div>
  );
}
